/**
 * API Client for connecting to the Frontier AI REST API
 * This handles interaction with the Hono API endpoints for persistent data
 */

import type { Call, CallStatus } from "@frontierx/types";

interface PaginatedCallsResponse {
  calls: Call[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

interface CreateCallRequest {
  orgId: string;
  userId: string;
  summary?: string;
  startTime: string;
  endTime: string;
  meetingUrl?: string;
  originatingCalendarEventId?: string;
  callScriptId?: string;
}

interface UpdateCallRequest {
  summary?: string;
  startTime?: string;
  endTime?: string;
  meetingUrl?: string;
  status?: CallStatus;
  callScriptId?: string;
}

interface ApiError {
  error: string;
  message?: string;
  details?: unknown;
}

declare const __API_URL__: string;

/**
 * API Client for interacting with the Frontier AI REST API
 */
export class ApiClient {
  private readonly baseUrl: string;
  private jwt: string | null = null;

  constructor() {
    // Access the same __API_URL__ global that the WebSocket code uses
    // This ensures we use the same backend URL as the WebSocket connection

    this.baseUrl = __API_URL__;
  }

  /**
   * Set the JWT token for authentication
   */
  setJwt(token: string | null) {
    this.jwt = token;
  }

  /**
   * Get headers with authentication
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.jwt) {
      headers.Authorization = `Bearer ${this.jwt}`;
    }

    return headers;
  }

  /**
   * Get the API URL
   * All API routes now use direct /api/... paths
   */
  private getApiUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get calls with pagination and filtering
   */
  async getCalls(
    options: {
      page?: number;
      limit?: number;
      status?: CallStatus;
      userId?: string;
      orgId?: string;
      from?: Date;
      to?: Date;
    } = {},
  ): Promise<PaginatedCallsResponse> {
    const params = new URLSearchParams();

    // Add query parameters if defined
    if (options.page) params.append("page", options.page.toString());
    if (options.limit) params.append("limit", options.limit.toString());
    if (options.status) params.append("status", options.status);
    if (options.userId) params.append("userId", options.userId);
    if (options.orgId) params.append("orgId", options.orgId);
    if (options.from) params.append("from", options.from.toISOString());
    if (options.to) params.append("to", options.to.toISOString());

    const queryString = params.toString();
    const url = `${this.getApiUrl()}/api/calls${
      queryString ? `?${queryString}` : ""
    }`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching calls:", error);
      throw error;
    }
  }

  /**
   * Get a specific call by ID
   */
  async getCall(id: string): Promise<Call> {
    const url = `${this.getApiUrl()}/api/calls/${id}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      const result = await response.json();

      // Cache this call for future requests
      try {
        const call = result.call;
        if (call?.id && call?.orgId) {
          const cachedCallsJson =
            localStorage.getItem("frontierx_calls") ?? "[]";
          const cachedCalls = JSON.parse(cachedCallsJson);

          // Update or add this call to the cache
          const existingIndex = cachedCalls.findIndex(
            (c: Call) => c.id === call.id,
          );
          if (existingIndex >= 0) {
            cachedCalls[existingIndex] = call;
          } else {
            cachedCalls.push(call);
          }

          // Store in local storage (limit to 100 calls)
          localStorage.setItem(
            "frontierx_calls",
            JSON.stringify(cachedCalls.slice(-100)),
          );
        }
      } catch (e) {
        console.warn("Could not cache call:", e);
      }

      return result.call;
    } catch (error) {
      console.error(`Error fetching call ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new call
   */
  async createCall(data: CreateCallRequest): Promise<Call> {
    const url = `${this.baseUrl}/api/calls`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      const result = await response.json();
      return result.call;
    } catch (error) {
      console.error("Error creating call:", error);
      throw error;
    }
  }

  /**
   * Update an existing call
   */
  async updateCall(id: string, data: UpdateCallRequest): Promise<Call> {
    const url = `${this.baseUrl}/api/calls/${id}`;

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      const result = await response.json();
      return result.call;
    } catch (error) {
      console.error(`Error updating call ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete/cancel a call
   */
  async deleteCall(id: string): Promise<void> {
    const url = `${this.baseUrl}/api/calls/${id}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }
    } catch (error) {
      console.error(`Error deleting call ${id}:`, error);
      throw error;
    }
  }

  /**
   * Restart a call that has ended
   * This creates a new bot for the same meeting URL and updates the call record
   */
  async restartCall(id: string): Promise<Call> {
    const url = `${this.baseUrl}/api/calls/${id}/restart`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      const result = await response.json();
      return result.call;
    } catch (error) {
      console.error(`Error restarting call ${id}:`, error);
      throw error;
    }
  }

  /**
   * Trigger analysis for a specific call
   */
  async triggerCallAnalysis(id: string): Promise<void> {
    const url = `${this.baseUrl}/api/calls/${id}/analysis/trigger`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({}), // Always send a valid JSON body
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }
    } catch (error) {
      console.error(`Error triggering analysis for call ${id}:`, error);
      throw error;
    }
  }

  /**
   * Handle error responses from the API
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    let errorData: ApiError;

    try {
      errorData = await response.json();
    } catch (parseError) {
      // If response is not JSON, create a generic error
      console.warn("Failed to parse error response as JSON:", parseError);
      errorData = {
        error: `HTTP Error ${response.status}`,
        message: response.statusText,
      };
    }

    const error = new Error(errorData.message || errorData.error);
    Object.assign(error, { statusCode: response.status, ...errorData });
    throw error;
  }
}

// Export a singleton instance for use throughout the app
export const apiClient = new ApiClient();
